import { type BillAction } from '@/types/bill'
import { Button } from "@/components/ui/button"
import { Eye, EyeOff, Printer, FileText, Loader2 } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface BulkActionBarProps {
  selectedCount: number
  currentTab: string
  onMonitor: () => void
  onIgnore: () => void
  onUnignore: () => void
  onAction: (action: BillAction) => void
  onOpenBulkPrintDialog?: () => void
  isProcessing?: boolean
  allSelectedHaveReviews?: boolean
}

export function BulkActionBar({
  selectedCount,
  currentTab,
  onMonitor,
  onIgnore,
  onUnignore,
  onAction,
  onOpenBulkPrintDialog,
  isProcessing = false,
  allSelectedHaveReviews = true
}: BulkActionBarProps) {
  if (selectedCount === 0) {
    return (
      <div className="w-full">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2 w-full justify-between">
              <span>Bulk Actions</span>
              <span className="text-xs text-muted-foreground">
                No bills selected
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem className="text-muted-foreground text-center" disabled>
              Select bills to perform actions
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-2 flex flex-col xs:flex-row items-start xs:items-center justify-between gap-2">
      <div className="px-2 xs:px-4 py-1">
        <span className="text-sm font-medium">
          {selectedCount} bill{selectedCount !== 1 ? 's' : ''} selected
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 w-full xs:w-auto justify-end px-2">
        {currentTab === 'New' && (
          <>
            <Button 
              size="sm" 
              variant="outline"
              onClick={onMonitor} 
              className="gap-1 bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap flex-shrink-0"
              disabled={isProcessing}
            >
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              Monitor
            </Button>
            <Button size="sm" variant="outline" onClick={onIgnore} className="gap-1 whitespace-nowrap flex-shrink-0" disabled={isProcessing}>
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />}
              Ignore
            </Button>
          </>
        )}
        {currentTab === 'Monitoring' && (
          <>
            <Button size="sm" variant="outline" onClick={() => onAction('LegislativeReview')} className="gap-1 whitespace-nowrap flex-shrink-0" disabled={isProcessing}>
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              AI Legislative Review
            </Button>
            <Button size="sm" variant="outline" onClick={onIgnore} className="gap-1 whitespace-nowrap flex-shrink-0" disabled={isProcessing}>
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />}
              Stop Monitoring
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>  {/* Wrapper div to allow tooltip on disabled button */}
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={onOpenBulkPrintDialog} 
                      className="gap-1 whitespace-nowrap flex-shrink-0" 
                      disabled={isProcessing || !allSelectedHaveReviews}
                    >
                      {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                      Print Reviews
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {!allSelectedHaveReviews 
                    ? "Cannot print reviews: one or more selected bills don't have AI reviews generated" 
                    : "Print reviews for all selected bills"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
        {currentTab === 'Ignored' && (
          <Button 
            size="sm" 
            variant="outline" 
            onClick={onUnignore}
            className="gap-1 whitespace-nowrap flex-shrink-0"
            disabled={isProcessing}
          >
            {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            Resume Monitoring
          </Button>
        )}
      </div>
    </div>
  )
}